import * as XLSX from 'xlsx';

import { formatPizzaSize, PizzaSummary, pizzaGroupTotals } from './dashboardDetailsPizzas';
import { productLineTotal } from './productLineTotal';

type Cell = string | number;
export type ExportSheet = { name: string; rows: Cell[][] };

interface ChartDataset {
  label: string;
  data: number[];
}

interface ExportChart {
  title: string;
  labels: string[];
  datasets: ChartDataset[];
}

export interface DashboardDetailsExportInput {
  periodLabel: string;
  sources: string[];
  sections: {
    deliveredOrders: {
      amountOfOrders: number;
      totalItems: number;
      totalDelivery: number;
    };
    withdrawnOrders: { amountOfOrders: number; totalValue: number };
    count: number;
    total: number;
    totalWithDelivery: number;
  };
  stages: {
    amountOfDeliveredOrders: number;
    amountOfDeniedOrders: number;
    amountOfCanceledOrders: number;
    total: number;
  };
  products: any[];
  pizzas: PizzaSummary;
  totalProducts: number;
  countProducts: number;
  complements: any[];
  groups: any[];
  paymentMethods: any[];
  totalPaymentMethods: number;
  countPaymentMethods: number;
  sales: ExportChart;
  coupons: ExportChart;
}

function num(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}

function share(part: number, whole: number): number {
  return whole ? round((part / whole) * 100) : 0;
}

function productOrigin(product: { ifood?: boolean; pdv?: boolean }): string {
  if (product.pdv) return 'Atac PDV';
  if (product.ifood) return 'iFood';
  return 'Atac';
}

function chartSheet(chart: ExportChart, empty: string): Cell[][] {
  if (chart.labels.length === 0 || chart.datasets.length === 0) {
    return [['Período'], [empty]];
  }

  const header = ['Período', ...chart.datasets.map((dataset) => dataset.label)];
  if (chart.datasets.length > 1) header.push('Total');

  return [
    header,
    ...chart.labels.map((label, index) => {
      const values = chart.datasets.map((dataset) => round(num(dataset.data[index])));
      const row: Cell[] = [label, ...values];
      if (chart.datasets.length > 1) {
        row.push(round(values.reduce((sum, value) => sum + value, 0)));
      }
      return row;
    }),
  ];
}

export function buildDashboardDetailsSheets(
  input: DashboardDetailsExportInput,
): ExportSheet[] {
  const { sections, stages } = input;
  const pizzaTotals = pizzaGroupTotals(input.pizzas);

  const summary: Cell[][] = [
    ['Detalhes do dashboard'],
    ['Período', input.periodLabel],
    ['Fontes', input.sources.join(', ')],
    [],
    ['Vendas por setor', 'Vendas', 'Valor'],
    [
      'Delivery',
      num(sections.deliveredOrders.amountOfOrders),
      round(num(sections.deliveredOrders.totalItems)),
    ],
    [
      'Taxas de entrega',
      '',
      round(num(sections.deliveredOrders.totalDelivery)),
    ],
    [
      'Balcão',
      num(sections.withdrawnOrders.amountOfOrders),
      round(num(sections.withdrawnOrders.totalValue)),
    ],
    ['Total sem taxas de entrega', num(sections.count), round(num(sections.total))],
    [
      'Total com taxas de entrega',
      num(sections.count),
      round(num(sections.totalWithDelivery)),
    ],
    [],
    ['Estágios dos pedidos', 'Pedidos', '%'],
    [
      'Entregues',
      num(stages.amountOfDeliveredOrders),
      share(num(stages.amountOfDeliveredOrders), num(stages.total)),
    ],
    [
      'Negados',
      num(stages.amountOfDeniedOrders),
      share(num(stages.amountOfDeniedOrders), num(stages.total)),
    ],
    [
      'Cancelados',
      num(stages.amountOfCanceledOrders),
      share(num(stages.amountOfCanceledOrders), num(stages.total)),
    ],
    [],
    ['Produtos no total', num(input.countProducts), round(num(input.totalProducts))],
    [
      'Finalizações no total',
      num(input.countPaymentMethods),
      round(num(input.totalPaymentMethods)),
    ],
  ];

  const products: Cell[][] = [['Produto', 'Origem', 'Quantidade', 'Total', '% da quantidade']];
  input.products.forEach((product) => {
    if (product.kind === 'pizzas') {
      products.push([
        'Pizzas',
        'Atac / Atac PDV',
        num(pizzaTotals.quantity),
        round(num(pizzaTotals.total)),
        share(num(pizzaTotals.quantity), num(input.countProducts)),
      ]);
      return;
    }

    products.push([
      String(product.produto_descricao ?? product.name ?? '').trim() || 'Sem descrição',
      productOrigin(product),
      num(product.quantity),
      round(productLineTotal(product)),
      share(num(product.quantity), num(input.countProducts)),
    ]);
  });

  const pizzas: Cell[][] = [['Tamanho', 'Tipo', 'Descrição', 'Quantidade', 'Total']];
  input.pizzas.sizes.forEach((size) => {
    const label = formatPizzaSize(size.size);
    pizzas.push([label, 'Tamanho', label, num(size.quantity), round(num(size.total))]);
    size.flavors.forEach((flavor) =>
      pizzas.push([label, 'Sabor', flavor.name, num(flavor.quantity), round(num(flavor.total))]),
    );
    size.complements.forEach((complement) =>
      pizzas.push([
        label,
        'Complemento',
        complement.name,
        num(complement.quantity),
        round(num(complement.total)),
      ]),
    );
  });

  const complementsQuantity = input.complements.reduce(
    (sum, complement) => sum + num(complement.quantity),
    0,
  );
  const complements: Cell[][] = [['Complemento', 'Quantidade', 'Total', '% da quantidade']];
  [...input.complements]
    .sort((a, b) => num(b.quantity) - num(a.quantity))
    .forEach((complement) => {
      complements.push([
        String(complement.nomeproduto ?? '').trim(),
        num(complement.quantity),
        round(num(complement.quantity) * num(complement.value)),
        share(num(complement.quantity), complementsQuantity),
      ]);
    });

  const groups: Cell[][] = [['Grupo', 'Total']];
  [...input.groups]
    .sort((a, b) => num(b.total) - num(a.total))
    .forEach((group) => groups.push([String(group.descricao ?? ''), round(num(group.total))]));

  const payments: Cell[][] = [['Finalizadora', 'Origem', 'Finalizações', 'Total', '% das finalizações']];
  [...input.paymentMethods]
    .map((payment) => ({
      name: String(
        (payment.ifood ? payment.name : payment.forma_pagamento_descricao) ?? '',
      ),
      origin: payment.ifood ? 'iFood' : 'Atac / Atac PDV',
      count: num(payment.ifood ? payment.quantity : payment.count),
      total: num(payment.ifood ? payment.total : payment.sum),
    }))
    .sort((a, b) => b.count - a.count)
    .forEach((payment) =>
      payments.push([
        payment.name,
        payment.origin,
        payment.count,
        round(payment.total),
        share(payment.count, num(input.countPaymentMethods)),
      ]),
    );

  return [
    { name: 'Resumo', rows: summary },
    { name: 'Produtos', rows: products },
    { name: 'Pizzas', rows: pizzas },
    { name: 'Complementos', rows: complements },
    { name: 'Vendas', rows: chartSheet(input.sales, 'Sem vendas no período') },
    { name: 'Vendas por grupo', rows: groups },
    { name: 'Finalizadoras', rows: payments },
    { name: 'Cupons de desconto', rows: chartSheet(input.coupons, 'Sem cupons no período') },
  ];
}

export function buildDashboardDetailsFileName(
  startDateKey: string,
  endDateKey: string,
): string {
  return startDateKey === endDateKey
    ? `detalhes-dashboard-${startDateKey}.xlsx`
    : `detalhes-dashboard-${startDateKey}-a-${endDateKey}.xlsx`;
}

export function exportDashboardDetailsWorkbook(
  sheets: ExportSheet[],
  fileName: string,
): void {
  const workbook = XLSX.utils.book_new();

  sheets.forEach((sheet) => {
    const worksheet = XLSX.utils.aoa_to_sheet(sheet.rows);
    const widths = sheet.rows.reduce<number[]>((acc, row) => {
      row.forEach((cell, index) => {
        acc[index] = Math.max(acc[index] ?? 10, Math.min(String(cell).length + 2, 60));
      });
      return acc;
    }, []);
    worksheet['!cols'] = widths.map((wch) => ({ wch }));
    XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name);
  });

  XLSX.writeFile(workbook, fileName);
}
